import { useRef, useEffect } from "react";
import { getYouTubeIdFromBlock } from "../utils/youtube";
import TwitterTweetEmbed from "../pages/blog/SanityComponents/TwitterTweetEmbed";
import CodeBlock from "../pages/blog/SanityComponents/CodeBlock";
import Poll from "../pages/blog/SanityComponents/VotingPoll";
import CustomTweet from "../pages/blog/SanityComponents/CustomTweet";
import { CiShare1 } from "react-icons/ci";
import { urlForImage, urlForImageWithVanity } from "../lib/sanityImageUrl";
import YoutubeEmbed from "../pages/blog/SanityComponents/YoutubeEmbed";
import Tooltip from "../pages/blog/SanityComponents/Tooltip";
import FaqSection from "../pages/blog/SanityComponents/FaqSection";
import BlockquoteComponent from "../pages/blog/SanityComponents/BlockquoteComponent";
import TipComponent from "../pages/blog/SanityComponents/TipComponent";
import ProsConsComponent from "../pages/blog/SanityComponents/ProsConsComponent";
import { createSlug } from "../utils/tableOfContents";

// Helper function to extract YouTube ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;

  // Match YouTube URL patterns
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

// Helper function to get the best available alt text
const getImageAlt = (value) => {
  // Priority: local alt -> asset altText -> asset title -> asset description -> fallback
  return (
    value.alt || // Local alt text set on image field
    value.asset?.altText || // Asset-level alt text from media management
    value.asset?.title || // Asset-level title from media management
    value.asset?.description || // Asset-level description from media management
    ""
  );
};

// Helper function to get the best available caption
const getImageCaption = (value) => {
  // Priority: local caption -> asset description -> asset title
  return (
    value.caption || // Local caption set on image field
    value.asset?.description || // Asset-level description from media management
    value.asset?.title || // Asset-level title from media management
    null
  );
};

// Helper function to get the best available title (for tooltip)
const getImageTitle = (value) => {
  // Priority: asset title -> local caption -> asset description -> alt text
  return (
    value.asset?.title || // Asset-level title from media management
    value.caption || // Local caption
    value.asset?.description || // Asset-level description from media management
    getImageAlt(value) ||
    ""
  );
};

const ExplainPortableTextComponents = {
  types: {
    codeBlock: ({ value }) => <CodeBlock value={value} />,
    tweetEmbed: ({ value }) => <TwitterTweetEmbed value={value} />,
    tip: ({ value }) => <TipComponent value={value} />,
    prosConsTable: ProsConsComponent,
    pollReference: ({ value }) => {
      return <Poll value={value} />;
    },
    youtubeEmbed: ({ value }) => {
      console.log("YouTube embed value:", value);

      const videoId = getYouTubeIdFromBlock(value);

      return <YoutubeEmbed embedId={videoId} />;
    },
    customBlockquote: ({ value }) => <BlockquoteComponent value={value} />,
    image: ({ value }) => {
      console.log("Image value with asset fields:", value);

      const altText = getImageAlt(value);
      const caption = getImageCaption(value);
      const titleText = getImageTitle(value);

      // Debug log to see what fields are available
      console.log("Asset fields:", {
        assetAltText: value.asset?.altText,
        assetTitle: value.asset?.title,
        assetDescription: value.asset?.description,
        localCaption: value.caption,
        localAlt: value.alt,
        finalAlt: altText,
        finalCaption: caption,
        finalTitle: titleText,
      });

      return (
        <figure className='my-6'>
          <img
            src={urlForImageWithVanity(value)}
            alt={altText}
            title={titleText}
            className='rounded-lg max-w-full h-auto blog-image-content'
          />
        </figure>
      );
    },
    customTweet: ({ value }) => {
      if (!value) return null;

      return (
        <CustomTweet
          content={value.content}
          author={{
            name: value.author.name,
            handle: value.author.handle,
            avatar: value.author.avatar?.asset
              ? urlForImage(value.author.avatar).url()
              : "/default-avatar.png",
          }}
          date={value.date}
          likes={value.likes || 0}
          retweets={value.retweets || 0}
        />
      );
    },
    faqSectionBlock: ({ value }) => <FaqSection value={value} />,
  },
  block: {
    h1: ({ children }) => {
      // Convert children to text to create the slug
      const text = children
        .map((child) => (typeof child === "string" ? child : ""))
        .join("");

      // Create a slug for the ID
      const slug = createSlug(text);
      console.log(`Setting h1 ID: ${slug} for "${text}"`);

      return (
        <h1 id={slug} className='text-3xl font-bold my-4'>
          {children}
        </h1>
      );
    },
    h2: ({ children }) => {
      // Convert children to text to create the slug
      const text = children
        .map((child) => (typeof child === "string" ? child : ""))
        .join("");

      // Create a slug for the ID
      const slug = createSlug(text);
      console.log(`Setting h2 ID: ${slug} for "${text}"`);

      return (
        <h2 id={slug} className='text-2xl font-bold my-3'>
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      // Convert children to text to create the slug
      const text = children
        .map((child) => (typeof child === "string" ? child : ""))
        .join("");

      // Create a slug for the ID
      const slug = createSlug(text);
      console.log(`Setting h3 ID: ${slug} for "${text}"`);

      return (
        <h3 id={slug} className='text-xl font-bold my-3'>
          {children}
        </h3>
      );
    },
    h4: ({ children }) => {
      // Convert children to text to create the slug
      const text = children
        .map((child) => (typeof child === "string" ? child : ""))
        .join("");

      // Create a slug for the ID
      const slug = createSlug(text);
      console.log(`Setting h4 ID: ${slug} for "${text}"`);

      return (
        <h4 id={slug} className='text-lg font-bold my-2'>
          {children}
        </h4>
      );
    },
    normal: ({ children }) => {
      return <p className='my-4'>{children}</p>;
    },
    blockquote: ({ children }) => (
      <blockquote className='border-l-4 border-gray-300 pl-4 my-4 italic'>
        {children}
      </blockquote>
    ),
  },
  marks: {
    link: ({ children, value }) => {
      return (
        <a
          href={value?.href}
          target='_blank'
          rel='noopener noreferrer'
          className='text-gray-500 inline-flex items-center gap-1 hover:border-b hover:border-purple'
        >
          {children}
          <CiShare1 className='inline text-xs mt-1 mr-1 ' />
        </a>
      );
    },
    tooltip: ({ children, value }) => {
      return <Tooltip tooltipText={value.text}>{children}</Tooltip>;
    },
  },
  list: {
    // For numbered lists
    number: ({ children }) => (
      <ol className='list-decimal pl-6 my-4 space-y-2'>{children}</ol>
    ),
    // For bullet lists
    bullet: ({ children }) => (
      <ul className='list-square pl-6 my-4 space-y-2'>{children}</ul>
    ),
  },
  listItem: {
    // For normal list items
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
  // Add a wrapper component to manage heading IDs
  wrapper: ({ children, value }) => {
    const ref = useRef();

    useEffect(() => {
      // Add an effect to ensure IDs are applied to headings
      const applyIdsToHeadings = () => {
        const headings = document.querySelectorAll("h1, h2, h3, h4");
        console.log(`Found ${headings.length} headings to check for IDs`);

        headings.forEach((heading) => {
          if (!heading.id) {
            const text = heading.textContent.trim();
            const slug = createSlug(text);
            console.log(`Setting missing ID "${slug}" on heading "${text}"`);
            heading.id = slug;
          }
        });
      };

      // Run after a slight delay to ensure components are rendered
      const timeout = setTimeout(applyIdsToHeadings, 500);

      return () => {
        clearTimeout(timeout);
      };
    }, [value]);

    return <div ref={ref}>{children}</div>;
  },
};

export default ExplainPortableTextComponents;
